// =============================================================================
// FeedbackSystem.jsx - Feedback and Bug Report Component
// =============================================================================
// Extracted from Settings.jsx to manage user feedback including:
// - Feedback modal with categorized topics
// - Email form submission via Netlify Forms
// - Fallback mailto: link if submission fails
// - Auto-populated user email and current URL
// =============================================================================

import React, { useState } from 'react';

// =============================================================================
// FEEDBACK TOPIC OPTIONS
// =============================================================================
const FEEDBACK_TOPICS = [
  'Bug Report',
  'Feature Request',
  'User Interface Issue',
  'Performance Issue',
  'Data/Sync Problem',
  'Login/Authentication Issue',
  'Settings Issue',
  'Team Configuration Issue',
  'Pitch Configuration Issue',
  'Import/Export Issue',
  'General Feedback',
  'Other'
];

// =============================================================================
// FEEDBACK MODAL COMPONENT
// =============================================================================
const FeedbackModal = ({ 
  isOpen, 
  onClose, 
  userEmail, 
  clubName,
  onSubmitSuccess 
}) => {
  const [form, setForm] = useState({
    from: userEmail || '',
    topic: '',
    content: '',
    url: window.location.href
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!form.from || !form.topic || !form.content) {
      alert('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);

    try {
      // Try Netlify Forms submission
      const formData = new FormData();
      formData.append('form-name', 'feedback-form');
      formData.append('email', form.from);
      formData.append('club', clubName || 'Unknown');
      formData.append('topic', form.topic);
      formData.append('message', form.content);
      formData.append('url', form.url);

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData).toString()
      });

      if (response.ok) {
        alert('Thank you! Your feedback has been sent successfully. We\'ll review it soon.');
        onSubmitSuccess?.();
        onClose();
        // Reset form
        setForm({
          from: userEmail || '',
          topic: '',
          content: '',
          url: window.location.href
        });
      } else {
        throw new Error(`Server responded with status: ${response.status}`);
      }
    } catch (error) {
      console.error('Netlify Forms submission failed:', error);
      alert('Direct submission failed. Opening your email client as backup...');
      
      // Fallback to mailto:
      const subject = `Pitchallo Feedback: ${form.topic}`;
      const body = `From: ${form.from}
Club: ${clubName || 'Unknown'}
Topic: ${form.topic}
URL: ${form.url}

Message:
${form.content}

---
Sent from Pitchallo Feedback Form`;

      const mailtoLink = `mailto:giannini000@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      
      try {
        window.location.href = mailtoLink;
        setTimeout(() => {
          alert('Your email client should have opened.');
        }, 1000);
        onClose();
        // Reset form
        setForm({
          from: userEmail || '',
          topic: '',
          content: '',
          url: window.location.href
        });
      } catch (mailtoError) {
        console.error('Mailto also failed:', mailtoError);
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        width: '100%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 25px rgba(0, 0, 0, 0.25)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#1f2937',
            margin: 0
          }}>
            Feedback / Report a Bug
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '4px'
            }}
          >
            ×
          </button>
        </div>

        {/* Form Fields */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Email Field */}
          <div>
            <label style={{
              display: 'block',
              fontSize:'14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px'
            }}>
              From (Your Email)
            </label>
            <input
              type="email"
              value={form.from}
              onChange={(e) => handleInputChange('from', e.target.value)}
              placeholder="your.email@example.com"
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Topic Field */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px'
            }}>
              Topic
            </label>
            <select
              value={form.topic}
              onChange={(e) => handleInputChange('topic', e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                backgroundColor: 'white'
              }}
            >
              <option value="">Select a topic...</option>
              {FEEDBACK_TOPICS.map((topic, index) => (
                <option key={index} value={topic}>
                  {topic}
                </option>
              ))}
            </select>
          </div>

          {/* URL Field */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px'
            }}>
              Page URL
            </label>
            <input
              type="text"
              value={form.url}
              onChange={(e) => handleInputChange('url', e.target.value)}
              placeholder="The page where you encountered this issue"
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                backgroundColor: '#f9fafb',
                fontFamily: 'monospace',
                color: '#6b7280'
              }}
              readOnly={false}
            />
            <p style={{
              fontSize: '12px',
              color: '#9ca3af',
              marginTop: '4px',
              marginBottom: '0'
            }}>
              Auto-filled with current page URL
            </p>
          </div>
          
          {/* Message Field */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px'
            }}>
              Message
            </label>
            <textarea
              value={form.content}
              onChange={(e) => handleInputChange('content', e.target.value)}
              placeholder="Please describe your feedback, bug report, or suggestion in detail..."
              rows={6}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
          </div>
        </div>

        {/* Footer Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          marginTop: '24px',
          paddingTop: '16px',
          borderTop: '1px solid #e5e7eb'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              backgroundColor: 'white',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer',
              color: '#374151'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{
              padding: '8px 16px',
              backgroundColor: isSubmitting ? '#9ca3af' : '#22c55e',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              color: 'white',
              fontWeight: '500',
              opacity: isSubmitting ? 0.7 : 1
            }}
          >
            {isSubmitting ? 'Sending...' : 'Send Feedback'}
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// FEEDBACK SYSTEM COMPONENT
// =============================================================================
const FeedbackSystem = ({ 
  userEmail, 
  clubName,
  onSubmitSuccess
}) => {
  const [showModal, setShowModal] = useState(false);

  const openModal = () => {
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  return (
    <>
      {/* This component is typically triggered from a button in the parent */}
      {/* Export the openModal function for parent to use */}
      <FeedbackModal
        isOpen={showModal}
        onClose={closeModal}
        userEmail={userEmail}
        clubName={clubName}
        onSubmitSuccess={onSubmitSuccess}
      />
    </>
  );
};

// Export both the system and the modal for flexibility
export default FeedbackSystem;
export { FeedbackModal, FEEDBACK_TOPICS };
